// Shared bookmarklet builders. Extracted from EmbedCodeGenerator so the
// activation checklist (dashboard) can offer the same "Try it on your
// site" preview without either duplicating this construction logic or
// mounting a second copy of EmbedCodeGenerator (which would trigger its own
// apiService.getApiKey() fetch on top of the checklist's own).

// Production defaults, overridable per environment (e.g. the staging portal)
export const MATCH_API_URL = process.env.REACT_APP_MATCH_API_URL || 'https://api.imgaff.com/api/match-image';
export const WIDGET_CDN_URL = process.env.REACT_APP_WIDGET_CDN_URL || 'https://cdn.imgaff.com/imgaff.min.js';

// Origins for the embed snippet's preconnect hints. Derived from the URLs above rather
// than hardcoded so a staging portal hints at ITS OWN hosts — a hint pointing at prod
// while the widget talks to staging opens a useless socket and warms nothing.
// Falls back to the raw value if URL parsing fails (relative override in a test env).
function originOf(url: string, fallback: string): string {
  try {
    return new URL(url, typeof window !== 'undefined' ? window.location.href : undefined).origin;
  } catch {
    return fallback;
  }
}

export const API_ORIGIN = originOf(MATCH_API_URL, 'https://api.imgaff.com');
export const CDN_ORIGIN = originOf(WIDGET_CDN_URL, 'https://cdn.imgaff.com');

// Install-free preview: injects the CDN widget with the publisher's key into
// whatever page it's clicked on. Guarded so a second click is a no-op.
/**
 * `overrides` carries the SAME non-default `overlay` / `targeting` object the
 * pasted snippet carries, and passing it is the whole point.
 *
 * Until 2026-09-06 this took the API key alone, so the preview always ran the
 * widget's DEFAULTS. The Install page sits six appearance controls — position,
 * display mode, trigger, max products, colour, minimum image size — directly
 * above a button that says "overlays appear on matching images", and that button
 * ignored every one of them. A publisher who chose breadcrumb mode and previewed
 * saw thumbnails, which is worse than having no preview: it answers the question
 * confidently and wrongly.
 *
 * Optional because ActivationChecklist offers the same preview with no controls
 * anywhere near it — there, defaults ARE the honest answer.
 */
export function buildBookmarkletHref(apiKey: string, overrides?: Record<string, unknown>): string {
  const config = { apiKey, apiUrl: MATCH_API_URL, debug: true, ...(overrides ?? {}) };
  const code =
    `(function(){` +
    `if(window.__imgaffPreview){alert('ImgAff preview is already running on this page.');return}` +
    `window.__imgaffPreview=1;` +
    `var s=document.createElement('script');` +
    `s.src=${JSON.stringify(WIDGET_CDN_URL)};` +
    `s.onload=function(){new window.ImgAff(${JSON.stringify(config)})};` +
    `document.body.appendChild(s)` +
    `})()`;
  return `javascript:${encodeURIComponent(code)}`;
}

// Read-only inspector: does NOT construct a new widget instance (no API key
// needed at all). It finds the widget already running on the page
// (window.__imgaffDiagnostics facade, set by the widget itself — see
// packages/script/src/index.ts) and calls its read-only diagnostics hook to
// outline every <img> with a status/reason badge. Nothing is uploaded or
// persisted (lightweight version: no attempts ledger, no capture upload —
// see design.md's explicit non-goals).
export function buildInspectorBookmarkletHref(): string {
  const code =
    `(function(){` +
    `var w=window.__imgaffDiagnostics;` +
    `if(!w||typeof w.diagnoseAllImages!=='function'){` +
    `alert('ImgAff widget not detected on this page. Install the embed snippet first and make sure the page has finished loading.');` +
    `return}` +
    `var LABELS={` +
    `'not-loaded':'not loaded','too-small':'too small','bad-aspect-ratio':'bad aspect ratio',` +
    `'missing-include-class':'missing include class','excluded-class':'excluded class','has-handlers':'has inline handler','linked-image':'wrapped in a link'};` +
    `document.querySelectorAll('.imgaff-inspector-badge').forEach(function(b){b.remove()});` +
    `w.diagnoseAllImages().forEach(function(r){` +
    `var img=r.img;` +
    `var color=r.matched?'#28a745':(r.eligible?'#ffc107':'#dc3545');` +
    `img.style.outline='3px solid '+color;` +
    `img.style.outlineOffset='2px';` +
    `var label=r.matched?'matched':(r.eligible?(r.sent?'sent \\u2014 no match':'eligible'):'skipped: '+(LABELS[r.reason]||r.reason));` +
    `var badge=document.createElement('div');` +
    `badge.className='imgaff-inspector-badge';` +
    `badge.textContent=label;` +
    `badge.style.cssText='position:absolute;z-index:2147483647;background:'+color+';color:#fff;font:11px sans-serif;padding:2px 6px;border-radius:3px;pointer-events:none;';` +
    `var rect=img.getBoundingClientRect();` +
    `badge.style.top=(window.scrollY+rect.top)+'px';` +
    `badge.style.left=(window.scrollX+rect.left)+'px';` +
    `document.body.appendChild(badge)` +
    `})` +
    `})()`;
  return `javascript:${encodeURIComponent(code)}`;
}
